// src/components/ReferenceField.tsx
//
// The Reference picker on the Client and Vendor forms. Shared deliberately:
// ClientsPage.tsx and VendorsPage.tsx are ~2700-line near-duplicates and drift
// the moment a field is written into both by hand.
//
// The value is the reference NAME, not an id — that is what the create/update
// payload carries, and the backend get-or-creates it inside the same
// transaction as the client write.
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { SearchableCombobox } from './ui/SearchableCombobox';
import { referencesApi } from '../api/references';

interface ReferenceFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export default function ReferenceField({ value, onChange, disabled }: ReferenceFieldProps) {
  const [options, setOptions] = useState<string[]>([]);

  useEffect(() => {
    let alive = true;
    // referencesApi.list resolves to [] on failure — an empty dropdown that
    // still accepts a typed name beats blocking the form with an error.
    referencesApi.list().then((rows) => {
      if (alive) setOptions(rows.map((r) => r.name));
    });
    return () => { alive = false; };
  }, []);

  // A freshly typed name is not in `options` yet; include it so the trigger
  // shows a checkmark and the combobox stops offering to add it again.
  const merged = value && !options.some((o) => o.toLowerCase() === value.toLowerCase())
    ? [value, ...options]
    : options;

  return (
    <div className="cm-field">
      <label className="cm-field-label">Reference</label>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <SearchableCombobox
            value={value}
            onChange={onChange}
            options={merged}
            allowCustom
            disabled={disabled}
            matchTriggerWidth
            placeholder="Select or add a reference…"
            searchPlaceholder="Search references…"
            emptyText="No references yet — type a name to add one."
          />
        </div>
        {value && !disabled && (
          <button
            type="button"
            aria-label="Clear reference"
            onClick={() => onChange('')}
            className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-50 disabled:hover:bg-transparent"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
